let sharedAudioContext = null;

const getAudioContext = () => {
  if (!sharedAudioContext) {
    sharedAudioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return sharedAudioContext;
};

// Browsers block all audio - including AudioContext.resume() - until the page
// has received at least one real user gesture (click/key/touch). The alarm
// triggered automatically by the alert-polling timer has no such gesture, so
// without this it is silently blocked while a manually-clicked "Test Sound"
// button always works (the click IS the gesture). Call this once (see
// AlertContext) to unlock the shared context on the user's first interaction
// with the page - it then stays usable programmatically for the rest of the
// session, including for the automatic alarm.
export const unlockAudioOnFirstInteraction = () => {
  const events = ["click", "keydown", "touchstart"];
  const unlock = () => {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    events.forEach((evt) => document.removeEventListener(evt, unlock));
  };

  events.forEach((evt) => document.addEventListener(evt, unlock));
  return () => events.forEach((evt) => document.removeEventListener(evt, unlock));
};

// Plays a repeating two-tone alarm beep pattern for `durationSeconds` on the
// shared context. Returns the oscillators so the caller can stop them early
// via stopAlarmBeeps(). Does NOT close the context - closing would destroy
// the unlock from unlockAudioOnFirstInteraction() and require a fresh user
// gesture before any future alarm could play again.
export const playAlarmBeeps = async (durationSeconds = 10) => {
  const audioContext = getAudioContext();

  if (audioContext.state === "suspended") {
    try {
      await audioContext.resume();
    } catch (e) {
      // Still locked (no user gesture yet this session) - nothing more to do.
    }
  }

  const beepDuration = 0.15;
  const pauseDuration = 0.1;
  const cycleTime = beepDuration + pauseDuration;
  const numBeeps = Math.floor(durationSeconds / cycleTime);
  const oscillators = [];

  for (let i = 0; i < numBeeps; i++) {
    const startTime = audioContext.currentTime + i * cycleTime;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = i % 2 === 0 ? 880 : 660;
    oscillator.type = "square";

    gainNode.gain.setValueAtTime(0.3, startTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + beepDuration);

    oscillator.start(startTime);
    oscillator.stop(startTime + beepDuration);
    oscillators.push(oscillator);
  }

  return oscillators;
};

export const stopAlarmBeeps = (oscillators = []) => {
  oscillators.forEach((osc) => {
    try {
      osc.stop();
    } catch (e) {
      // Already stopped.
    }
  });
};
